import _ from 'lodash';
import { Permission, ResourceId, ResourceType, roles, rolesPermissions } from '../constants';
import { IUser } from '../redux/types';
import { RoleId } from '../types';

export type PermissionCheckResult = 'allowed' | 'signIn' | 'permissionDenied';

// create a flat list of permissions by traversing role->includesRole tree. optionally filter by resource type.
export function getAllPermissions(
    roles: RoleId[],
    resourceType?: ResourceType,
    permissions: Permission[] = []
): Permission[] {
    return roles.reduce((sum: Permission[], roleId) => {
        const rolePemissionObject = rolesPermissions[roleId];
        if (!rolePemissionObject) {
            return permissions;
        }
        if (rolePemissionObject.permissions) {
            if (resourceType) {
                permissions.push(..._.filter(rolePemissionObject.permissions, { type: resourceType }));
            } else {
                permissions.push(...rolePemissionObject.permissions);
            }
        }
        if (rolePemissionObject.includesRoles) {
            // eslint-disable-next-line no-param-reassign
            permissions = getAllPermissions(rolePemissionObject.includesRoles, resourceType, permissions);
        }
        return permissions;
    }, permissions);
}

function checkSinglePermission(
    permission: Permission,
    resourceId: string,
    permissionResource: ResourceId = permission.resourceId
) {
    const caseNormalizedPermissionResource = permissionResource.toLowerCase();
    const caseNormalizedResourceId = resourceId.toLowerCase();

    // check exact match
    if (caseNormalizedPermissionResource === caseNormalizedResourceId) {
        return true;
    }
    // check mask match
    else if (
        caseNormalizedPermissionResource.endsWith('*') &&
        caseNormalizedResourceId.startsWith(
            caseNormalizedPermissionResource.substring(0, caseNormalizedPermissionResource.length - 1)
        )
    ) {
        return true;
    }
    // no match
    else {
        return false;
    }
}

// check if all of given resources are in the user's permission set. optionally filter permission set by resource type.
export function checkPermission(
    resourceIds: ResourceId[],
    user?: IUser,
    resourceType?: ResourceType
): PermissionCheckResult {
    const roleIds: RoleId[] = user?.roles ?? [roles.unauthenticatedUser.id];
    const allPermissions: Permission[] = getAllPermissions(roleIds, resourceType);
    const isAllowed = resourceIds.every((resourceId) =>
        allPermissions.some((permission) => checkSinglePermission(permission, resourceId))
    );

    if (isAllowed) {
        return 'allowed';
    } else if (user) {
        return 'permissionDenied';
    } else {
        return 'signIn';
    }
}

export function cognitoGroupsToRoleIds(groups: string[]): RoleId[] {
    return _.chain(groups)
        .map((group) => _.findKey(roles, { cognitoGroup: group }) as RoleId)
        .compact()
        .value();
}

// Returns the set of all roles inherited (directly or transitively) by the given roles.
// Does NOT include the input roles themselves.
export function getInheritedRoles(roleIds: RoleId[]): Set<RoleId> {
    const inherited = new Set<RoleId>();
    function collect(id: RoleId) {
        const included = rolesPermissions[id]?.includesRoles;
        if (included) {
            included.forEach((child) => {
                if (!inherited.has(child)) {
                    inherited.add(child);
                    collect(child);
                }
            });
        }
    }
    roleIds.forEach(collect);
    return inherited;
}

// Removes redundant roles that are already inherited by other roles in the list.
export function simplifyRoles(roleIds: RoleId[]): RoleId[] {
    const inherited = getInheritedRoles(roleIds);
    return roleIds.filter((r) => !inherited.has(r));
}
